

import React, { useEffect, useState } from 'react';
import { 
  Table, 
  Button, 
  Card, 
  Space, 
  InputNumber, 
  message, 
  Modal,
  Typography,
  Tag,
  Divider,
  Spin
} from 'antd';
import { 
  EditOutlined, 
  DollarOutlined, 
  BankOutlined,
  UserOutlined
} from '@ant-design/icons';
import { getPayRoles, updatePayroleApi, distributePayrollApi } from '../../services/payrole';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';

const { Title, Text } = Typography;

export default function PayrollDistribution() {
  let { companyId } = useParams();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [editingKey, setEditingKey] = useState('');
  const [editValue, setEditValue] = useState(0);
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    getPays();
  }, []);

  const getPays = async () => {
    setLoading(true);
    try {
      let res = await getPayRoles({ companyId: companyId });
      console.log(res, "payrolls");
      
      // Handle both response formats
      if (res?.success) {
        setPayrollData(res.data || []);
      } else {
        setPayrollData(res || []);
      }
    } catch (error) {
      console.error('Error fetching payrolls:', error);
      toast.error('Failed to fetch payroll data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (key, currentAdvance) => {
    setEditingKey(key);
    // Get current advance amount from payroll records
    const employee = payrollData.find(emp => emp._id === key);
    const currentAdvanceAmount = employee?.payrollRecords?.[0]?.advanceAmount || 0;
    setEditValue(currentAdvanceAmount);
  };

  const handleSave = async (key) => {
    setUpdating(true);
    try {
      const employee = payrollData.find(item => item._id === key);
      
      if (!employee) {
        toast.error('Employee not found');
        return;
      }

      // Get payroll record ID if exists
      const payrollRecordId = employee?.payrollRecords?.[0]?._id;
      
      const updateData = {
        advanceAmount: editValue,
        name: employee.name,
        companyId: companyId,
        salary: employee.salary || 0,
        ...(payrollRecordId && { _id: payrollRecordId }) // Include payroll record ID if exists
      };

      console.log('Updating payroll for employee:', key, updateData);

      let res = await updatePayroleApi(key, updateData);
      
      if (res?.success) {
        toast.success('Advance amount updated successfully!');
        
        // Update local state with new calculation
        const updatedData = payrollData.map(item => {
          if (item._id === key) {
            const currentCommission = item.payrollRecords?.[0]?.commission || 0;
            const newTotalPay = (item.salary || 0) + currentCommission - editValue;
            
            return {
              ...item,
              payrollRecords: item.payrollRecords.length > 0 ? [{
                ...item.payrollRecords[0],
                advanceAmount: editValue,
                totalPay: newTotalPay
              }] : [{
                advanceAmount: editValue,
                commission: 0,
                totalPay: (item.salary || 0) - editValue,
                salary: item.salary || 0
              }]
            };
          }
          return item;
        });
        
        setPayrollData(updatedData);
        setEditingKey('');
        message.success('Payroll updated successfully!');
      } else {
        toast.error(res?.message || 'Failed to update payroll');
      }
    } catch (error) {
      console.error('Error updating payroll:', error);
      toast.error('Failed to update advance amount');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setEditingKey('');
    setEditValue(0);
  };

const [isDistributeModalVisible, setIsDistributeModalVisible] = useState(false);
const [distributing, setDistributing] = useState(false);

// Replace your handleDistribute function with this:
const handleDistribute = () => {
  console.log('Distribute clicked');
  
  if (selectedRowKeys.length === 0) {
    message.warning('Please select employees to distribute payroll');
    return;
  }

  setIsDistributeModalVisible(true);
};

// Add this new function for the actual distribution
const performDistribution = async () => {
  setDistributing(true);
  try {
    console.log('Distributing payroll for employees:', selectedRowKeys);
    
    const res = await distributePayrollApi({
      employeeIds: selectedRowKeys,
      companyId: companyId
    });

    console.log('Distribution response:', res);

    if (res?.success) {
      toast.success(`Payroll distributed successfully to ${selectedRowKeys.length} employees!`);
      setSelectedRowKeys([]);
      setIsDistributeModalVisible(false);
      await getPays();
    } else {
      toast.error(res?.message || 'Failed to distribute payroll');
    }
  } catch (error) {
    console.error('Distribution error:', error);
    toast.error('Failed to distribute payroll. Please try again.');
  } finally {
    setDistributing(false);
  }
};

// Add this function to calculate employee data for modal
const getSelectedEmployeeData = () => {
  const selectedEmployees = payrollData.filter(item =>
    selectedRowKeys.includes(item._id)
  );

  const employeesWithPay = selectedEmployees.map(emp => {
    const payrollRecord = emp.payrollRecords?.[0];
    let employeePay = 0;
    
    if (payrollRecord) {
      const salary = emp.salary || 0;
      const commission = payrollRecord.commission || 0;
      const advance = payrollRecord.advanceAmount || 0;
      employeePay = salary + commission - advance;
    } else {
      employeePay = emp.salary || 0;
    }
    
    return {
      ...emp,
      calculatedPay: employeePay
    };
  });

  const totalAmount = employeesWithPay.reduce((sum, emp) => sum + emp.calculatedPay, 0);

  return { employeesWithPay, totalAmount };
};
const DistributeModal = () => {
  const { employeesWithPay, totalAmount } = getSelectedEmployeeData();

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <DollarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          Confirm Payroll Distribution
        </div>
      }
      open={isDistributeModalVisible}
      onOk={performDistribution}
      onCancel={() => setIsDistributeModalVisible(false)}
      width={600}
      okText="Distribute Payroll"
      cancelText="Cancel"
      confirmLoading={distributing}
      okButtonProps={{
        size: 'large',
        icon: <DollarOutlined />
      }}
      cancelButtonProps={{
        size: 'large'
      }}
    >
      <div style={{ padding: '16px 0' }}>
        {/* Summary Section */}
        <div style={{ 
          marginBottom: '20px', 
          padding: '16px', 
          backgroundColor: '#f6ffed', 
          borderRadius: '8px',
          border: '1px solid #b7eb8f'
        }}>
          <div style={{ marginBottom: '8px' }}>
            <Text strong style={{ color: '#52c41a' }}>Selected Employees: </Text>
            <Text style={{ fontSize: '16px' }}>{employeesWithPay.length}</Text>
          </div>
          <div>
            <Text strong style={{ color: '#1890ff' }}>Total Distribution Amount: </Text>
            <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
              ₹{totalAmount?.toLocaleString() || '0'}
            </Text>
          </div>
        </div>

        {/* Employee List */}
        <div style={{ marginBottom: '20px' }}>
          <Text strong style={{ marginBottom: '12px', display: 'block', fontSize: '16px' }}>
            Employee Breakdown:
          </Text>
          <div style={{ 
            maxHeight: '250px', 
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            backgroundColor: '#fafafa'
          }}>
            {employeesWithPay.map((emp, index) => (
              <div 
                key={emp._id || index}
                style={{ 
                  padding: '12px 16px', 
                  borderBottom: index < employeesWithPay.length - 1 ? '1px solid #f0f0f0' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa'
                }}
              >
                <div>
                  <Text strong>{emp.name || 'Unknown Employee'}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Base: ₹{emp.salary?.toLocaleString() || '0'}
                  </Text>
                </div>
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  ₹{emp.calculatedPay?.toLocaleString() || '0'}
                </Text>
              </div>
            ))}
          </div>
        </div>

        {/* Warning Section */}
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#fff7e6', 
          borderRadius: '6px',
          border: '1px solid #ffd591'
        }}>
          <Text type="warning" style={{ fontSize: '13px' }}>
            <strong>⚠️ Important:</strong> This action will mark the selected payrolls as distributed and cannot be undone. 
            Please ensure all details are correct before proceeding.
          </Text>
        </div>
      </div>
    </Modal>
  );
};
const formatDateToMMDDYYYY = (dateValue) => {
  if (!dateValue) {
    return null; // or return '' if you prefer empty string
  }
  
  try {
    // Handle different date formats
    let date;
    
    if (moment.isMoment(dateValue)) {
      // If it's already a moment object
      date = dateValue;
    } else if (dateValue instanceof Date) {
      // If it's a JavaScript Date object
      date = moment(dateValue);
    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      // If it's a string or timestamp
      date = moment(dateValue);
    } else {
      // Fallback for other formats
      date = moment(dateValue);
    }
    
    // Check if the date is valid
    if (!date.isValid()) {
      return null;
    }
    
    // Format to MM-DD-YYYY
    return date.format('MM-DD-YYYY');
    
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
};

// Usage examples:
// const formattedDate = formatDateToMMDDYYYY(record?.payrollRecords[0]?.createdAt);
// console.log(formattedDate); // Output: "08-13-2024" (example)
  const columns = [
     {
      title: 'Date of month',
      dataIndex: 'dt',
      key: 'dt',
      render: (text,record) => (
        <Space>
         
          <Text strong>{formatDateToMMDDYYYY(record?.payrollRecords[0]?.createdAt)|| '-'}</Text>
        </Space>
      ),
    },
    {
      title: 'Employee Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <UserOutlined />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Base Salary',
      dataIndex: 'salary',
      key: 'salary',
      render: (amount) => (
        <Tag color="blue">₹{amount?.toLocaleString() || 0}</Tag>
      ),
    },
    {
      title: 'Bank Details',
      dataIndex: 'bankDetails',
      key: 'bankDetails',
      render: (bank, record) => {
        let bankString = 'Not Provided';
        if (record?.bankDetails?.bankName) {
          const accountNumber = record?.bankDetails?.accountNumber || '';
          const maskedAccount = accountNumber.length > 4 
            ? `****${accountNumber.slice(-4)}` 
            : accountNumber;
          bankString = `${record.bankDetails.bankName} - ${maskedAccount}`;
        }
        
        return (
          <Space>
            <BankOutlined />
            <Text>{bankString}</Text>
          </Space>
        );
      }
    },
    {
      title: 'Advance Amount',
      dataIndex: 'advanceAmount',
      key: 'advanceAmount',
      render: (amount, record) => {
        const isEditing = editingKey === record._id;
        const advanceAmount = record?.payrollRecords?.[0]?.advanceAmount || 0;
        
        return isEditing ? (
          <Space>
            <InputNumber
              value={editValue}
              onChange={setEditValue}
              min={0}
              formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/₹\s?|(,*)/g, '')}
              style={{ width: 120 }}
            />
            <Button 
              type="primary" 
              size="small" 
              onClick={() => handleSave(record._id)}
              loading={updating}
            >
              Save
            </Button>
            <Button 
              size="small" 
              onClick={handleCancel}
              disabled={updating}
            >
              Cancel
            </Button>
          </Space>
        ) : (
          <Tag color={advanceAmount > 0 ? "orange" : "green"}>
            ₹{advanceAmount?.toLocaleString() || 0}
          </Tag>
        );
      },
    },
    {
      title: 'Commission Earned',
      dataIndex: 'commission',
      key: 'commission',
      render: (amount, record) => {
        const commission = record?.payrollRecords?.[0]?.commission || 0;
        return (
          <Tag color="green">+₹{commission?.toLocaleString() || 0}</Tag>
        );
      }
    },
    {
      title: 'Net Pay',
      dataIndex: 'totalPay',
      key: 'totalPay',
      render: (amount, record) => {
        const payrollRecord = record?.payrollRecords?.[0];
        let netPay = record?.salary || 0; // Base salary as fallback
        
        if (payrollRecord) {
          // If payroll record exists, use calculated total pay
          const salary = record?.salary || 0;
          const commission = payrollRecord.commission || 0;
          const advance = payrollRecord.advanceAmount || 0;
          netPay = salary + commission - advance;
        }
        
        return (
          <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
            ₹{netPay?.toLocaleString() || 0}
          </Tag>
        );
      }
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const hasPayrollRecord = record?.payrollRecords?.length > 0;
        const status = record?.payrollRecords?.[0]?.paid || 'no-activity';
        
        let color = 'default';
        let text = 'No Activity';
        
        if (hasPayrollRecord) {
          switch (status) {
            case 'pending':
              color = 'orange';
              text = 'Pending';
              break;
            case 'completed':
              color = 'green';
              text = 'Paid';
              break;
            default:
              color = 'blue';
              text = 'Active';
          }
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const isEditing = editingKey === record._id;
        const isPaid = record?.payrollRecords?.[0]?.paid === 'completed';
        
        return !isEditing && !isPaid ? (
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record._id, record.advanceAmount)}
            disabled={editingKey !== '' || updating}
          >
            Edit Advance
          </Button>
        ) : isPaid ? (
          <Text type="secondary">Paid</Text>
        ) : null;
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      console.log(newSelectedRowKeys, "selected keys");
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: editingKey !== '' || record?.payrollRecords?.[0]?.paid === 'completed',
    }),
  };

  const selectedEmployeesCount = selectedRowKeys.length;
  const totalDistributionAmount = payrollData
    .filter(item => selectedRowKeys.includes(item._id))
    .reduce((sum, emp) => {
      const payrollRecord = emp.payrollRecords?.[0];
      if (payrollRecord) {
        const salary = emp.salary || 0;
        const commission = payrollRecord.commission || 0;
        const advance = payrollRecord.advanceAmount || 0;
        return sum + (salary + commission - advance);
      }
      return sum + (emp.salary || 0);
    }, 0);

  if (loading) {
    return (
      <div style={{ 
        padding: '24px', 
        backgroundColor: '#f5f5f5', 
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            <DollarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Payroll Distribution
          </Title>
          <Text type="secondary">
            Manage employee salaries, commissions, and advance payments
          </Text>
        </div>

        {selectedRowKeys.length > 0 && (
          <div style={{ 
            marginBottom: '16px', 
            padding: '16px', 
            backgroundColor: '#e6f7ff', 
            borderRadius: '6px',
            border: '1px solid #91d5ff'
          }}>
            <Space size="large">
              <Text strong>
                Selected: {selectedEmployeesCount} employee{selectedEmployeesCount !== 1 ? 's' : ''}
              </Text>
              <Text strong>
                Total Amount: ₹{totalDistributionAmount?.toLocaleString()}
              </Text>
              <Button 
                type="primary" 
                icon={<DollarOutlined />}
                onClick={handleDistribute}
                size="large"
              >
                Distribute Payroll
              </Button>
            </Space>
          </div>
        )}

        <Table
          rowKey="_id" 
          rowSelection={rowSelection}
          columns={columns}
          dataSource={payrollData}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} employees`,
          }}
          scroll={{ x: 1200 }}
          size="middle"
          loading={updating}
        />
      </Card>
      <DistributeModal/>
    </div>
  );
}